import { useContext } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { AuthContext } from "./auth/Auth";
import { useLoginState } from "./studioxr/auth/LoginState";
import { lazyWithRetry } from "./utils/lazyWithRetry";

// /materials/:id — every material's own page, one canonical URL for all
// visitors (the MCP deep link is one consumer; utm_source=mcp in analytics).
// Logged-in users get the page inside the Home shell; logged-out visitors get
// the same content under the slim public chrome, with auth challenges on the
// actions that need an account. No redirects in either state, so the landing
// URL (and its utm params) reaches GA4 untouched.

// Same module as App.jsx's lazy HomeLayout, so the chunk is shared.
const HomeLayoutLazy = lazyWithRetry(() =>
  import("./Home/HomeLayout").then((m) => ({ default: m.HomeLayout })),
);
const PublicMaterialPageLazy = lazyWithRetry(() =>
  import("./modals/product-details/PublicMaterialPage").then((m) => ({
    default: m.PublicMaterialPage,
  })),
);

export const MaterialPage = () => {
  const { currentUser } = useContext(AuthContext);
  const showLogin = useLoginState((state) => state.showLogin);
  // Hold the public wrapper while the signup wizard is open: Firebase sets
  // currentUser at account creation, before the profile step — swapping to
  // the shell then unmounts the wrapper's LoginModal and resets the wizard.
  // SignUp's completeRedirect skips /materials/* and calls setShowLogin(null)
  // on finish, which releases this hold and swaps to the shell in place.
  return currentUser && showLogin !== "signup" ? (
    <HomeLayoutLazy />
  ) : (
    <PublicMaterialPageLazy />
  );
};

/**
 * /home/materials/:id was the page's URL while it lived inside the /home
 * routing tree — redirect any link minted then to the canonical URL.
 */
export const HomeMaterialsRedirect = () => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/materials/${id}${search}`} replace />;
};
